import { useState } from 'react';
import { AccountInfo } from '../components/ssm/ssm.types';
import { displayPhoneNumberFormat, getPostalCountry } from '../util/format';
import {
  PremiseSearchResponse,
  QuickAddressSearchParams,
} from '../__generated__/pge-types';
import useQas from './useQas';
import moment from 'moment';
import { useSSMStopSubmit } from './useSSMStopSubmit';

export type Address = {
  addressLine1?: string;
  city?: string;
  state?: string;
  postal?: string;
  country?: string;
  qasVerified?: boolean;
};

export enum CustContactClass {
  PROG = 'PROG',
  WSS = 'WSS',
  PYMT = 'PYMT',
  REQ = 'REQ',
  OUT = 'OUT',
}

export enum CustContactType {
  ENROLL = 'ENROLL',
  PAPLESBIL = 'PAPLESBIL',
  CPSWRD = 'CPSWRD',
  FPSWRD = 'FPSWRD',
  PAYEXT = 'PAYEXT',
  TPA = 'TPA',
  CANCEL = 'CANCEL',
  EQUALPAY = 'EQUALPAY',
  QUICKPAY = 'QUICKPAY',
  AUTOPAY = 'AUTOPAY',
  CHGEM = 'CHGEM',
  RES15EMAI = 'RES15EMAI',
  RES15 = 'RES15',
  UPDTINF = 'UPDTINF',
  ALERTS = 'ALERTS',
  GUESTPAY = 'GUESTPAY',
  REPOUT = 'REPOUT',
  WEBCK = 'WEBCK',
  SSMOVOUT = 'SSMOVOUT',
  SSMOVIN = 'SSMOVIN',
  SSMOVE = 'SSMOVE',
  RENEW = 'RENEW',
  PTR = 'PTR',
  CHGPIN = 'CHGPIN',
  PREFDD = 'PREFDD',
  TODINELG = 'TODINELG',
  TODBUD = 'TODBUD',
  TODENR = 'TODENR',
}

export enum CustContactCharacteristicType {
  CMPROG = 'CM-PROG',
  CMCHNNL = 'CM-CHNNL',
  CMPPELG = 'CM-PPELG',
  CMAUTO = 'CM-AUTO',
  CMPPOPT = 'CM-PPOPT',
  CMCLEAN = 'CM-CLEAN',
  CMGFS = 'CM-GFS',
  CMGS = 'CM-GS',
  CMHAB = 'CM-HAB',
  CMREFER = 'CM-REFER',
  CMPROMO = 'CM-PROMO',
  CMDISTR = 'CM-DISTR',
  CMRCOMP = 'CM-RCOMP',
}

export enum CustContactCharacteristicValue {
  CSWEB = 'CSWEB',
  ENROLL = 'ENROLL',
  UNENROLL = 'UNENROLL',
  CHECKING = 'CHECKING',
  OPTION3 = 'OPTION3',
  QPAY = 'QPAY',
  CHANGE = 'CHANGE',
  YES = 'YES',
  NO = 'NO',
  PENDING = 'PENDING',
  INELIGIBLE = 'INELIGIBLE',
  BIZWEB = 'BIZWEB',
}

export type StopEligibility = {
  isPendingDisconnect?: boolean;
  isDisconnected?: boolean;
  hasMultiplePremises?: boolean;
  hasNoActiveElectricSA?: boolean;
  hasProtectedAddress?: boolean;
  hasPendingStop?: boolean;
  isEqualPay?: boolean;
};

export enum eligibilityKey {
  isPendingDisconnect = 'isPendingDisconnect',
  isDisconnected = 'isDisconnected',
  hasMultiplePremises = 'hasMultiplePremises',
  hasNoActiveElectricSA = 'hasNoActiveElectricSA',
  hasProtectedAddress = 'hasProtectedAddress',
}

export type DateAndAdress = {
  date?: Date;
  addressLine1?: string;
  city?: string;
  state?: string;
  zip?: string;
  inCareOf?: string;
  phone?: string;
  country?: string;
  qasVerified?: boolean;
  stayingBehind?: string;
};

export type AdditionalContext = {
  t: (translation: string) => string;
};

export type StopDate = {
  stopDate?: Date;
};

export type AddressAndDate = {
  address?: Address;
  date?: Date;
};

export type AccountNumber = {
  account?: string;
};

export type SSMStopServiceState = {
  addressAndDate: DateAndAdress;
  account: AccountInfo;
};

export default () => {
  const { search } = useQas();
  const [ssmStopServiceState, setSSMStopServiceState] = useState<
    SSMStopServiceState
  >({
    addressAndDate: {},
    account: {},
  });

  const { submit } = useSSMStopSubmit();

  const setAddressAndDate = (addressAndDate: DateAndAdress) => {
    setSSMStopServiceState(state => ({
      ...state,
      addressAndDate,
    }));
  };

  const setAccountDetails = (account: AccountInfo) => {
    setSSMStopServiceState(state => ({
      ...state,
      account,
    }));
  };

  const doVerifyAddress = async (homeDetails: DateAndAdress) => {
    const { addressLine1, city, state, zip } = homeDetails;
    const params: QuickAddressSearchParams = {
      city: city,
      state: state,
      addressLine1: addressLine1,
      postal: zip,
      isMailingAddress: true,
      country: zip ? getPostalCountry(zip) : '',
    };
    const results = await search(params);
    return { results, params };
  };
  const onConfirm = async (premiseIds: PremiseSearchResponse[]) => {
    const stopServicePayload = {
      stopDate: moment(ssmStopServiceState?.addressAndDate?.date).format(
        'YYYY-MM-DD',
      ),
      premId: String(premiseIds[0]?.premId),
      finalBillAddress: {
        postal: ssmStopServiceState?.addressAndDate?.zip,
        addressLine1: ssmStopServiceState?.addressAndDate?.addressLine1,
        city: ssmStopServiceState?.addressAndDate?.city,
        state: ssmStopServiceState?.addressAndDate?.state,
        qasVerified: ssmStopServiceState?.addressAndDate?.qasVerified ?? false,
        inCareOf: ssmStopServiceState?.addressAndDate?.inCareOf,
        country: ssmStopServiceState?.addressAndDate?.country,
      },
      phoneNumber: displayPhoneNumberFormat(
        ssmStopServiceState?.addressAndDate?.phone?.replace(/[^0-9]/g, ''),
      ),
      acctId: ssmStopServiceState?.account?.accountNumber!,
      stayingBehind: ssmStopServiceState?.addressAndDate?.stayingBehind!,
    };
    const result = await submit(stopServicePayload);
    return result;
  };

  return {
    ssmStopServiceState,
    setAddressAndDate,
    doVerifyAddress,
    onConfirm,
    setAccountDetails,
    setSSMStopServiceState,
  };
};
